/**
 * <scent-search>: command-palette search inside the #search-modal dialog.
 *
 * Empty field  -> quick chips, recently viewed (Horizon's localStorage list) and popular scents.
 * Typing       -> debounced Predictive Search API call, rendered server-side by
 *                 sections/scent-search-results.liquid and swapped into the results panel.
 * Keyboard     -> ⌘K / Ctrl+K or "/" opens it from anywhere, ↓/↑ walk through chips and
 *                 results, typing while a result is focused goes back to the field.
 * Families     -> Shopify's tokenizer drops some Turkish words (e.g. "Çiçeksi" matches nothing),
 *                 so a term equal to a scent family searches the `aile:<family>` product tag instead.
 */
const RESULTS_SECTION_ID = 'scent-search-results';
const RECENT_STORAGE_KEY = 'viewedProducts';
const SEARCH_DELAY = 180;
const LOADING_DELAY = 120;
const FAMILY_TAG_PREFIX = 'aile:';

class ScentSearch extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  #debounce = 0;
  /** @type {Map<string, string>} */
  #cache = new Map();
  /** @type {string | null} */
  #recentKey = null;

  connectedCallback() {
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-search-input]'));
    this.panels = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-panel]')));
    this.results = /** @type {HTMLElement | null} */ (this.querySelector('[data-panel="results"]'));
    this.recent = /** @type {HTMLElement | null} */ (this.querySelector('[data-recent]'));
    this.allLink = /** @type {HTMLAnchorElement | null} */ (this.querySelector('[data-search-all]'));
    this.status = this.querySelector('[data-search-status]');
    this.host = this.closest('dialog-component');

    this.input?.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('click', this.#onClick);
    this.host?.addEventListener('dialog:open', this.#onOpen);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.#onInput);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('click', this.#onClick);
    this.host?.removeEventListener('dialog:open', this.#onOpen);
    this.#controller?.abort();
  }

  #onOpen = () => {
    requestAnimationFrame(() => {
      this.input?.focus();
      this.input?.select();
    });
    this.#loadRecent();
  };

  #onInput = () => {
    if (!this.input) return;
    clearTimeout(this.#debounce);
    const term = this.input.value.trim();
    this.#updateAllLink(term);

    if (!term) {
      this.#controller?.abort();
      this.removeAttribute('data-loading');
      this.#show('idle');
      return;
    }

    this.#debounce = window.setTimeout(() => this.#search(term), SEARCH_DELAY);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const chip = /** @type {HTMLElement | null} */ (target?.closest('[data-search-term]'));

    if (chip && this.input) {
      this.input.value = chip.dataset.searchTerm || '';
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.focus();
      return;
    }

    if (target?.closest('[data-recent-clear]')) {
      try {
        localStorage.removeItem(RECENT_STORAGE_KEY);
      } catch {
        // Storage can be unavailable (private mode); hiding the list is enough.
      }
      this.#recentKey = '';
      if (this.recent) this.recent.hidden = true;
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (!this.input) return;

    // Printable keys typed while a result has focus belong in the search field.
    const printable = event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;
    if (printable && document.activeElement !== this.input) {
      this.input.focus();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const items = this.#items();
    if (!items.length) return;
    event.preventDefault();

    const current = items.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (event.key === 'ArrowDown') {
      items[Math.min(current + 1, items.length - 1)]?.focus();
    } else if (current <= 0) {
      this.input.focus();
    } else {
      items[current - 1]?.focus();
    }
  };

  /** @param {string} term */
  async #search(term) {
    this.#controller?.abort();

    const cached = this.#cache.get(term);
    if (cached !== undefined) {
      this.#render(cached);
      return;
    }

    const controller = new AbortController();
    this.#controller = controller;
    this.setAttribute('data-loading', '');
    const loadingTimer = window.setTimeout(() => this.#show('loading'), LOADING_DELAY);

    try {
      const url = new URL(this.dataset.suggestUrl || '/search/suggest', location.origin);
      url.searchParams.set('q', this.#query(term));
      url.searchParams.set('resources[type]', 'product,article,query');
      url.searchParams.set('resources[limit]', '8');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', RESULTS_SECTION_ID);

      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`Search request failed with ${response.status}`);

      const markup = this.#extract(await response.text());
      this.#cache.set(term, markup);
      if (this.input?.value.trim() === term) this.#render(markup);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      this.#renderError();
    } finally {
      clearTimeout(loadingTimer);
      if (this.#controller === controller) this.removeAttribute('data-loading');
    }
  }

  /** Recently viewed products, fetched through the regular search endpoint by id. */
  async #loadRecent() {
    if (!this.recent) return;

    /** @type {string[]} */
    let ids = [];
    try {
      ids = JSON.parse(localStorage.getItem(RECENT_STORAGE_KEY) || '[]');
    } catch {
      ids = [];
    }

    const key = ids.join(',');
    if (key === this.#recentKey) return;
    this.#recentKey = key;

    const grid = this.recent.querySelector('[data-recent-grid]');
    if (!ids.length || !grid) {
      this.recent.hidden = true;
      return;
    }

    try {
      const url = new URL(this.dataset.searchUrl || '/search', location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('section_id', RESULTS_SECTION_ID);

      const response = await fetch(url);
      if (!response.ok) throw new Error(`Recently viewed request failed with ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = /** @type {HTMLElement[]} */ (Array.from(doc.querySelectorAll('.scent-search-card')));
      // Search does not keep the stored order, so restore "most recent first".
      cards.sort((a, b) => ids.indexOf(a.dataset.productId || '') - ids.indexOf(b.dataset.productId || ''));
      cards.forEach((card, index) => card.style.setProperty('--i', String(index)));

      grid.replaceChildren(...cards.slice(0, 4));
      this.recent.hidden = cards.length === 0;
    } catch {
      this.recent.hidden = true;
    }
  }

  /** @param {string} html */
  #extract(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector('.scent-search-results')?.outerHTML ?? '';
  }

  /** @param {string} markup */
  #render(markup) {
    if (!this.results) return;
    this.results.innerHTML = markup;
    this.#show('results');

    const count = this.results.querySelector('.scent-search-results')?.getAttribute('data-count') ?? '0';
    if (this.status) this.status.textContent = (this.dataset.statusTemplate || '').replace('[count]', count);
  }

  #renderError() {
    if (!this.results) return;
    const message = document.createElement('p');
    message.className = 'scent-search__empty';
    message.textContent = this.dataset.errorText || '';
    this.results.replaceChildren(message);
    this.#show('results');
  }

  /** @param {string} name */
  #show(name) {
    for (const panel of this.panels) panel.hidden = panel.dataset.panel !== name;
  }

  /** @returns {HTMLElement[]} Focusable chips and results in the visible panel. */
  #items() {
    const panel = this.panels.find((candidate) => !candidate.hidden);
    if (!panel) return [];
    return /** @type {HTMLElement[]} */ (
      Array.from(panel.querySelectorAll('[data-search-term], [data-search-item]')).filter(
        (element) => element.getClientRects().length > 0
      )
    );
  }

  /**
   * @param {string} term
   * @returns {string} The search query for a term: a family tag lookup when the term names a family.
   */
  #query(term) {
    const families = (this.dataset.families || '').split('|').filter(Boolean);
    const needle = term.toLocaleLowerCase('tr');
    const family = families.find((name) => name.toLocaleLowerCase('tr') === needle);
    return family ? `tag:"${FAMILY_TAG_PREFIX}${family}"` : term;
  }

  /** @param {string} term */
  #updateAllLink(term) {
    if (!this.allLink) return;
    const url = new URL(this.dataset.searchUrl || '/search', location.origin);
    url.searchParams.set('q', this.#query(term));
    url.searchParams.set('type', 'product');
    this.allLink.href = `${url.pathname}${url.search}`;
    this.allLink.hidden = !term;
  }
}

if (!customElements.get('scent-search')) customElements.define('scent-search', ScentSearch);

// ⌘K / Ctrl+K anywhere, or "/" when the visitor is not typing, opens the palette.
document.addEventListener('keydown', (event) => {
  const target = event.target;
  const typing =
    target instanceof HTMLElement && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
  const shortcut = ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') || (event.key === '/' && !typing);
  if (!shortcut) return;

  const host = /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (document.getElementById('search-modal'));
  if (typeof host?.showDialog !== 'function') return;

  event.preventDefault();
  host.showDialog();
});
